/**
 * Attack resolution. Turns a (weapon, attacker, target) triple into an
 * `AttackResult` and mutates the target's body. See CLAUDE.md > Body Simulation.
 *
 * Headless — no PixiJS.
 */

import { applyDamage, pickLimb, type BodyPart, type BodyState, type Severity } from './body';
import type { GuardState } from './guard';
import type { Weapon } from './weapons';

/** Minimum shape resolveAttack needs from an attacker — just position. */
export interface Attacker {
  x: number;
  y: number;
}

export interface AttackResult {
  fired: boolean;
  hit: boolean;
  killed: boolean;
  part?: BodyPart;
  damage: number;
  severity: Severity;
  /** Sound loudness that should be emitted at the attacker's tile. */
  loudness: number;
  /** Why the attack didn't fire (or hit), if applicable. */
  reason?: 'no-target' | 'cooldown' | 'no-ammo' | 'out-of-range' | 'missed';
}

export interface AttackTarget {
  x: number;
  y: number;
  body: BodyState;
  /** Guard state for knife-takedown gating; player is treated as always aware. */
  state?: GuardState;
}

const NO_HIT: Omit<AttackResult, 'fired' | 'loudness'> = {
  hit: false,
  killed: false,
  damage: 0,
  severity: 'graze',
};

/**
 * Resolve one attack. Mutates target.body on hit, sets weapon.cooldown, and
 * decrements ammo. Does NOT emit a sound event — the caller is expected to
 * push `attacker.x, attacker.y, result.loudness` into the sound queue if
 * `result.fired` is true.
 */
export function resolveAttack(
  attacker: Attacker,
  target: AttackTarget | null,
  weapon: Weapon,
  rng: () => number = Math.random,
): AttackResult {
  const base: AttackResult = {
    fired: false,
    hit: false,
    killed: false,
    damage: 0,
    severity: 'graze',
    loudness: 0,
  };

  if (!target) return { ...base, reason: 'no-target' };
  if (weapon.cooldown > 0) return { ...base, reason: 'cooldown' };
  if (!weapon.isMelee && !weapon.unlimitedAmmo && (weapon.ammo ?? 0) <= 0) {
    return { ...base, reason: 'no-ammo' };
  }

  const dist = Math.hypot(target.x - attacker.x, target.y - attacker.y);
  const maxRange = weapon.isMelee ? 1.5 : weapon.rangeTiles;
  if (dist > maxRange + 1e-6) return { ...base, reason: 'out-of-range' };

  weapon.cooldown = weapon.fireIntervalSec;
  if (!weapon.isMelee && !weapon.unlimitedAmmo && weapon.ammo !== undefined) weapon.ammo -= 1;
  base.fired = true;
  base.loudness = weapon.loudness;

  // Knife takedown: instant kill on unaware target.
  if (weapon.isMelee && target.state === 'unaware') {
    target.body.dead = true;
    return {
      ...base,
      hit: true,
      killed: true,
      part: 'head',
      damage: target.body.hp.head + target.body.hp.torso,
      severity: 'kill',
    };
  }

  // Hit probability.
  const p = hitChance(weapon, dist);
  if (rng() >= p) return { ...base, ...NO_HIT, reason: 'missed' };

  // Land the hit on a weighted-random part.
  const part = pickLimb(rng);
  const dmg = weapon.damage;
  const severity = applyDamage(target.body, part, dmg);
  return {
    ...base,
    hit: true,
    killed: severity === 'kill',
    part,
    damage: dmg,
    severity,
  };
}

/** Chance-to-hit — clamped to [0.05, 0.95]. Exported for guardCombat.ts,
 *  which spawns bullets rather than resolving hitscan directly. */
export function hitChance(w: Weapon, dist: number): number {
  if (w.isMelee) return 1;
  const isSmg = w.kind === 'smg';
  const base = isSmg ? 0.65 : 0.85;
  const rangePenalty = (dist / w.rangeTiles) * 0.3;
  const p = base - rangePenalty;
  return Math.max(0.05, Math.min(0.95, p));
}

/**
 * Shared can-fire + consume path for ranged weapons. Returns null if the
 * shot can't fire (melee weapon, on cooldown, out of ammo, out of range).
 * On success, marks the cooldown and decrements ammo, returning the sound
 * loudness + measured distance. Callers (player fire path + guardCombat)
 * then spawn a bullet with their own limb-pick strategy.
 */
export function attemptRangedShot(
  attacker: Attacker,
  target: { x: number; y: number },
  weapon: Weapon,
  opts?: { ignoreRange?: boolean },
): { loudness: number; distance: number } | null {
  if (weapon.isMelee) return null;
  if (weapon.cooldown > 0) return null;
  if (!weapon.unlimitedAmmo && (weapon.ammo ?? 0) <= 0) return null;
  const dist = Math.hypot(target.x - attacker.x, target.y - attacker.y);
  // `ignoreRange` lets the PLAYER shoot at anything it can focus on with a clear
  // line of fire — range is bounded by perception + LOS, not a hard cap (though
  // accuracy still falls off with distance via `hitChance`). Guards keep the cap.
  if (!opts?.ignoreRange && dist > weapon.rangeTiles + 1e-6) return null;
  weapon.cooldown = weapon.fireIntervalSec;
  if (!weapon.unlimitedAmmo && weapon.ammo !== undefined) weapon.ammo -= 1;
  return { loudness: weapon.loudness, distance: dist };
}
